import { useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";

import { GetApi, CacheApi } from "~/api";

export interface CacheManagerDialogProps {
  open?: boolean;
  onClose?: () => void;
}

/**
 * Dialog for managing cache entries.
 */
export function CacheManagerDialog(props: CacheManagerDialogProps) {
  const { open = false, onClose } = props;

  const cacheKeys = useQuery({
    queryKey: ["developer", "cache-keys"],
    queryFn: () => GetApi(CacheApi).getCacheKeys(),
    // Only fetch cache keys when the dialog is open
    enabled: open,
  });

  const clearCache = useMutation({
    mutationFn: async () => GetApi(CacheApi).clearCache(),
    onSuccess: () => {
      cacheKeys.refetch();
    },
  });

  // Reset state when the dialog is opened
  useEffect(() => {
    if (open) clearCache.reset();
  }, [open]);

  const keys = cacheKeys.data?.keys ?? [];

  const sharedKeys = keys.filter((key) => key.startsWith("shared"));
  const userKeys = keys.filter((key) => !key.startsWith("shared"));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Cache manager</DialogTitle>
      <DialogContent>
        {cacheKeys.isLoading && <CircularProgress />}
        {cacheKeys.isFetched && (
          <Stack gap={2}>
            <Typography color="text.secondary">
              List of keys that have been cached in-memory by the server on GCP.
            </Typography>
            <Stack>
              <Accordion>
                <AccordionSummary>
                  <b style={{ marginRight: "0.5ch" }}>Shared keys</b> (
                  {sharedKeys?.length})
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary" mb={1.5}>
                    These cache keys are shared between all users.
                  </Typography>
                  <Stack component="ul" mt={0} pl={3}>
                    {sharedKeys.map((key) => (
                      <Typography
                        key={key}
                        variant="body2"
                        component="li"
                        mb={1}
                        style={{ overflowWrap: "break-word", wordBreak: "break-all" }}
                      >
                        <code>{key}</code>
                      </Typography>
                    ))}
                  </Stack>
                </AccordionDetails>
              </Accordion>
              <Accordion>
                <AccordionSummary>
                  <b style={{ marginRight: "0.5ch" }}>User keys</b> ({userKeys?.length})
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary" mb={1.5}>
                    These cache keys are unique for each user.
                  </Typography>
                  <Stack component="ul" mt={0} pl={3}>
                    {userKeys.map((key) => (
                      <Typography
                        key={key}
                        variant="body2"
                        component="li"
                        mb={1}
                        style={{ overflowWrap: "break-word", wordBreak: "break-all" }}
                      >
                        <code>{key}</code>
                      </Typography>
                    ))}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            </Stack>
            <Stack direction="row" gap={2} sx={{ ml: "auto" }}>
              <Button
                variant="outlined"
                color="error"
                disabled={clearCache.isPending}
                onClick={() => clearCache.mutateAsync()}
              >
                Clear cache
              </Button>
              <Button onClick={onClose} disabled={clearCache.isPending}>
                Close
              </Button>
            </Stack>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}
